import * as icons from '../../../assets/icons'
import { ActivateButton } from '../components/Manager/ActivateButton'
import { BigImageComponent, BigImagePickerAndEditor } from './subcomponents'
import { Button, Stack, Typography } from '@dtx-company/design-system/src'
import { DeleteModeDrawer } from '../components/Manager/DeleteModeDrawer'
import { DeleteOutline, Save } from '@mui/icons-material'
import { FC } from 'react'
import { FlowpageProps } from '../types/FlowpageProps'
import { FormProps, StyledFormV2 as StyledForm } from '../components/Layout'
import { FormTitle } from '../components/Title'
import { InlineManagerCard, ManagerProps } from '../components/Manager/Manager'
import { LinkEditSection } from '../../../components/profile/Links/LinkEditSection'
import { LockedTemplateField } from '../../FlowpageTemplates/LockedTemplateField/LockedTemplateField'
import { ManagerSettings } from '../components/Manager/ManagerSettings'
import { MaskType } from '@dtx-company/true-common/src/types/config'
import { useBigImageFormState } from './hooks'
import { useManagerSettings } from '../components/Manager/useManagerSettings'
import Image from 'next/image'
import capitalize from 'lodash/capitalize'

export const maskTypeOptions = [
  { value: MaskType.CIRCLE, title: capitalize(MaskType.CIRCLE) },
  { value: MaskType.SQUARE, title: capitalize(MaskType.SQUARE) },
  { value: MaskType.RECTANGLE, title: capitalize(MaskType.RECTANGLE) }
]

export type BigImageFormProps = Pick<FormProps, 'curr' | 'handleClose' | 'order'>

export const Form: FC<BigImageFormProps> = ({ curr, order, handleClose }) => {
  const { formTitle, formValues, handleSubmit, setValue, deleteImageLink, imageErrorMessage } =
    useBigImageFormState({
      curr,
      order,
      handleClose
    })

  return (
    <StyledForm onSubmit={handleSubmit}>
      <Stack gap={2}>
        <FormTitle
          icon={<Image width={24} height={24} src={icons.photoOutlined} alt="" />}
          title={formTitle}
        />

        <BigImagePickerAndEditor
          imageUrl={formValues.image}
          setValue={setValue}
          id="big-image-picker"
        />

        {imageErrorMessage && <Typography>{imageErrorMessage}</Typography>}

        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Button fullWidth onClick={deleteImageLink} startIcon={DeleteOutline}>
            Delete Image
          </Button>
          <Button fullWidth startIcon={Save} color="tertiary" type="submit">
            Save Image
          </Button>
        </Stack>
      </Stack>
    </StyledForm>
  )
}

export const Flowpage: FC<FlowpageProps> = ({ link }: FlowpageProps) => (
  <BigImageComponent link={link} />
)

/**
 * Special Manager for the BigImage link (Image Section)
 * given the layout and preview of this section in the page
 * is quite different from DefaultManager (no title or description)
 * and need the image to occupy most of the space.
 */
export const Manager: FC<ManagerProps> = ({ link, handle, editWidget }: ManagerProps) => {
  const {
    onDeleteLink,
    deleteLinkModeActive,
    toggleDeleteMode,
    showEditLink,
    setShowEditLink,
    toggleActivate,
    isActive,
    hasViewOnlyAccess,
    toggleEditMode,
    childOnClick,
    isTemplateConfigLocked
  } = useManagerSettings(link, editWidget)

  return (
    <InlineManagerCard viewOnly={hasViewOnlyAccess || isTemplateConfigLocked}>
      {showEditLink ? (
        <LinkEditSection handleClose={() => setShowEditLink(false)} />
      ) : (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%', px: 4 }}
        >
          {!isTemplateConfigLocked && (
            <ManagerSettings
              onClickEdit={toggleEditMode}
              onClickDelete={toggleDeleteMode}
              disabled={hasViewOnlyAccess}
              viewOnly={hasViewOnlyAccess}
            />
          )}
          {isTemplateConfigLocked && <LockedTemplateField />}

          <Stack justifyContent="center" onClick={childOnClick}>
            <BigImageComponent isEditLinkPreview link={link} />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={4}>
            <ActivateButton
              viewOnly={hasViewOnlyAccess || isTemplateConfigLocked}
              toggleActivated={toggleActivate}
              activated={isActive}
            />

            {!hasViewOnlyAccess && handle}
          </Stack>
        </Stack>
      )}

      {!showEditLink && (
        <DeleteModeDrawer
          deleteModeActive={deleteLinkModeActive}
          toggleDeleteMode={toggleDeleteMode}
          onDelete={onDeleteLink}
        />
      )}
    </InlineManagerCard>
  )
}
